import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import InputMask from "react-input-mask";
import { format, isValid, parse } from "date-fns";
import { ptBR } from "date-fns/locale/pt-BR";

type DateInputMaskedProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
};

type MaskedInputProps = {
  value?: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ value, onClick, onChange, onBlur, placeholder, disabled, className }, ref) => (
    <InputMask
      mask="99/99/9999"
      maskPlaceholder={null}
      value={value ?? ""}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
    >
      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
        <input
          {...inputProps}
          ref={ref}
          type="text"
          onClick={onClick}
          className={className}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}
    </InputMask>
  )
);

MaskedInput.displayName = "MaskedInput";

const parseUiDate = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length !== 10) {
    return null;
  }
  const parsed = parse(trimmed, "dd/MM/yyyy", new Date());
  if (!isValid(parsed)) {
    return null;
  }
  return format(parsed, "dd/MM/yyyy") === trimmed ? parsed : null;
};

export const DateInputMasked: React.FC<DateInputMaskedProps> = ({
  value,
  onChange,
  onBlur,
  disabled = false,
  invalid = false,
  placeholder = "dd/mm/aaaa",
}) => {
  const selected = useMemo(() => parseUiDate(value), [value]);

  return (
    <DatePicker
      selected={selected}
      value={value}
      locale={ptBR}
      dateFormat="dd/MM/yyyy"
      showPopperArrow={false}
      popperPlacement="bottom-start"
      withPortal
      portalId="root"
      onChange={(date) => {
        if (!date) {
          onChange("");
          return;
        }
        onChange(format(date, "dd/MM/yyyy"));
      }}
      onChangeRaw={(event) => {
        const target = event.target as HTMLInputElement | null;
        onChange(target?.value ?? "");
      }}
      onBlur={onBlur}
      customInput={
        <MaskedInput
          className={`form-control ${invalid ? "is-invalid" : ""}`}
          disabled={disabled}
          placeholder={placeholder}
        />
      }
      disabled={disabled}
    />
  );
};
